import {asQueryString, useLocalStorage} from '@wallaby-ui/core';
import {DEFAULT_PAGE_SIZE, getDefaultMTOptions, getTableMaxHeight, MaterialTableWrapper, TableSorting} from '@wallaby-ui/document';
import * as React from 'react';

import {type FiltersData as DepartmentFiltersData} from './departmenttypes';
import {type FiltersData, InterventionOverviewReportFilter, ReportType, RowTypeWithTotal} from './types';
import {useReportTableColumns} from './useReportTableColumns';

type HospitalNetworkTableProps = {
  reportType: ReportType;
  data: RowTypeWithTotal[];
  isLoading: boolean;
  filters: FiltersData;
  sorting: TableSorting;
  onSortingChange: (sorting: TableSorting) => void;
  storageIdPrefix: string;
};

export const HospitalReportTable: React.FC<HospitalNetworkTableProps> = ({
  reportType,
  data,
  isLoading,
  filters,
  sorting,
  onSortingChange,
  storageIdPrefix,
}) => {
  const [defaultTableScroll, setDefaultTableScroll] = useLocalStorage(`${storageIdPrefix}.tableScroll`, 0);
  const columns = useReportTableColumns(sorting, filters.report, reportType);

  const handleOrderChange = (colIndex: number, direction: 'asc' | 'desc') => {
    const columnId = columns[colIndex].field as string;
    onSortingChange({
      field: columnId,
      direction,
    });
  };

  return (
    <MaterialTableWrapper
      style={{maxHeight: '100%'}}
      defaultScroll={{to: defaultTableScroll}}
      onScroll={(event) => setDefaultTableScroll((event.target as HTMLDivElement).scrollTop)}
      singleLineColumnLabels
      options={{
        ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
        paging: false,
      }}
      variant='big'
      totalRow='top'
      useMaxHeight
      columns={columns}
      data={data}
      isLoading={isLoading}
      totalCount={data.length}
      onOrderChange={handleOrderChange}
      controlledSort={sorting}
      centerLoadingSpinner
    />
  );
};

export const getReportTableLink = (data: RowTypeWithTotal, reportFilters: InterventionOverviewReportFilter, reportType: ReportType) => {
  const hospitalLinkFilters: Pick<FiltersData, 'report'> = {
    report: {
      dateFrom: reportFilters.dateFrom,
      dateTo: reportFilters.dateTo,
      itemType: reportFilters.itemType,
      measureType: reportFilters.measureType,
      organizationLevelType: 'PARENT_DEPARTMENT',
      organizationId: data.organizationId,
    },
  };
  const departmentLinkFilters: Pick<DepartmentFiltersData, 'report'> = {
    report: {
      dateFrom: reportFilters.dateFrom,
      dateTo: reportFilters.dateTo,
      measureType: reportFilters.measureType,
      organizationIds: reportFilters.organizationId ? [data.organizationId] : undefined,
      causesAggregationType: 'ALL',
      chartValueSelection: 'ABSOLUTE_COUNTS',
      chartTypeSelection: 'STACKED_LINE_CHART',
    },
  };
  if (!data.isTotalRow) {
    if (reportType === ReportType.NETWORK) {
      return `/hospitalReport?${asQueryString(hospitalLinkFilters)}`;
    }
    return `/departmentReport?${asQueryString(departmentLinkFilters)}`;
  }
  return undefined;
};
